/**
 * EVERY tuning knob of the job system and the model gate, in one file, grouped by who reads it.
 *
 * The point is that tuning should not be a treasure hunt: when an exam is slow, or the process is being
 * OOM-killed, or a re-grade takes too long to start, the number to look at is in here with the reasoning next
 * to it — not spread over five modules. Consumers import from here and hold no numbers of their own.
 *
 * Three kinds of thing live here, and the difference matters:
 *
 *  - CONSTANTS      — fixed decisions that only change with a code change.
 *  - CONFIGURABLE   — read from env at boot (see src/config.ts). Never re-read: a limit that changes under a
 *                     running scheduler makes its behaviour unreproducible.
 *  - DERIVED        — functions computed from the above and from the machine, because a hardcoded number
 *                     would be wrong on a different instance size. These are the ones that make the system
 *                     adapt on its own rather than needing an operator.
 *
 * Invalid config never takes the process down and never silently applies: it is logged and the default is used.
 */

import { logger } from '@adornis/base/logging.js';
import { config } from '../../../config.js';
import { serverValue } from './server-value.js';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// config parsing — shared by every section below
// ─────────────────────────────────────────────────────────────────────────────────────────────────────

type ConfigKey =
  | 'JOB_CONCURRENCY'
  | 'JOB_DRAIN_SEC'
  | 'JOB_MAX_RUNTIME_MIN'
  | 'LLM_MAX_CONCURRENT_CALLS'
  | 'LLM_EXPECTED_REPLICAS';

/**
 * EVERY config read in this file goes through here, and that funnel is the whole reason the browser guard is one
 * line rather than one per knob — including for knobs added later.
 *
 * Guarded because these keys are all declared `client: false`, so `config.get` throws `ClientAccessError` by design,
 * and this file is evaluated in the client bundle (see `server-value.ts` for why). Empty is already this file's
 * "not set" signal, so every limit falls back to its documented default — the browser gets a fully-formed set of
 * numbers it will never use, rather than a half-initialised module.
 */
const rawConfig = (key: ConfigKey): string => serverValue(key, () => String(config.get(key) ?? '').trim(), '');

/** A positive integer, or `null` to mean "not set — use the derived default". */
const positiveInt = (key: ConfigKey): number | null => {
  const raw = rawConfig(key);
  if (!raw) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) {
    logger.error({ key, raw }, 'Ignoring invalid job limit — expected a positive integer');
    return null;
  }
  return value;
};

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// runner.ts — how much work starts, and how fairly
// ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** How often due work is looked for. Also the floor on gate latency until a change-stream nudge exists. */
export const TICK_INTERVAL_MS = 2_000;

/** Exams inspected per tick. A bound, not a target — the scan is oldest-first, so the rest come next pass. */
export const EXAM_SCAN_LIMIT = 50;

/**
 * A job whose type this process does not know WAITS this long rather than being cancelled: during a rolling
 * deploy the older instances legitimately do not know a new type yet.
 */
export const UNKNOWN_TYPE_RETRY_MS = 60_000;

/**
 * HOW MANY JOBS RUN AT ONCE, per process. One integer, and deliberately not a budget.
 *
 * This replaced a heap-reservation system — see the header of `load.ts` for why it went. The short version: it
 * required every handler to declare its worst case, almost none did, so every type inherited the maximum and the
 * process throttled itself to about four concurrent jobs while the model gate was provisioned for thirty
 * concurrent calls.
 *
 * A COUNT IS ENOUGH BECAUSE THE COST IS NOW BOUNDED WHERE IT HAPPENS. The two fan-outs that could actually exhaust
 * memory — page rasterisation in `extractImageFromPDF`, per-region OCR in `task-enrichment.ts` — are limited at
 * their call sites by `createLimiter`, so one job no longer means an unbounded amount of work.
 *
 * WHAT IT IS ACTUALLY PROTECTING, since it is not memory: the model gate hands out permits, and a handler builds
 * its payload BEFORE it waits for one. So admitting every runnable row would leave hundreds of payloads resident
 * against thirty permits — the queue depth would move into our heap. This keeps the number of jobs holding a
 * payload in the same order as the number that can be making a call.
 *
 * NOT A SAFETY GUARANTEE, and that is intended. The process can still be killed, and the design accepts it:
 * leases and `refundAttempt` mean a killed worker costs retried work rather than lost work, and
 * `handBackOwnOrphans` returns this host's claims on boot instead of waiting out their leases. The real fix is a
 * separate worker process, where a death costs no client connection.
 */
export const JOB_CONCURRENCY = positiveInt('JOB_CONCURRENCY') ?? 25;

/**
 * How many of ONE exam's pending rows the runner reads per pass. A read bound, not a fairness rule.
 *
 * Large enough that a cohort's worth of answer-extraction rows is seen in one pass, small enough that a runaway
 * enqueue cannot make a single tick read the whole collection. Admission is what decides how many of them start —
 * see `JOB_CONCURRENCY`.
 */
export const PENDING_SCAN_LIMIT = 100;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// job-store.ts / sweeper.ts — leases, retries, retention
// ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The longest a job may hold its claim. Past this the sweeper takes it back, and **nothing can extend it.**
 *
 * This replaced a heartbeat-renewed lease (90 s, refreshed every 15 s), and the trade is worth spelling out
 * because it looks like a downgrade and is not:
 *
 *  - A heartbeat is a liveness signal for the WORKER, and its only unique benefit was noticing a hard crash in
 *    ~90 s instead of waiting this long. Everything else it appeared to cover is covered better here: a graceful
 *    shutdown is handled by `drainInFlightJobs`, and this deployment is a single container with a single Node
 *    process (see `production-compose.yml` and `dockerEntrypoint.sh`), so there is no sibling instance whose
 *    live claims a crashed peer's rows could be confused with.
 *  - It FAILED to cover the failure that actually matters. The heartbeat ran on its own interval, independent of
 *    the handler, so a handler hung forever kept renewing its lease and told the sweeper it was healthy — a job
 *    holding its lockKey permanently, invisible to every recovery path. A lease that cannot be renewed catches
 *    that by construction: this is now a real maximum job runtime.
 *  - It CAUSED a failure of its own. A transient database error during a heartbeat let the lease lapse while the
 *    job was perfectly fine, so the sweeper requeued live work and it ran twice. Removing the signal removes its
 *    false negatives.
 *
 * The cost is honest: after an ungraceful death (OOM kill, SIGKILL), that job's work waits up to this long
 * instead of ~90 s. If that latency ever hurts, lower this, or reclaim claimed rows at startup — which is safe
 * only while this stays a single-instance deployment, so it is deliberately not done implicitly.
 *
 * THIS IS THE FALLBACK, not the rule. A type declares its own budget via `JobDefinition.maxRuntimeMs` and a single
 * row may override that via `JobDoc.maxRuntimeMs`; this applies only where neither did. A job that reaches its limit
 * is abandoned by the worker running it, which writes the outcome itself — see `execute`.
 */
export const MAX_JOB_RUNTIME_MS = (positiveInt('JOB_MAX_RUNTIME_MIN') ?? 30) * 60_000;

/**
 * The hard ceiling on any per-type or per-row runtime budget.
 *
 * A guard rail, not a tuning knob, which is why it is a constant and not config. The runtime a job is granted decides
 * how long its row stays unreclaimable after an ungraceful death, and one of the two inputs is a **number read out of
 * the database** — so a fat-fingered `maxRuntimeMs: 30_000_000` would create a claim the sweeper will not touch for
 * most of a day, holding a `lockKey` and blocking every other job that needs the same resource. Four hours is far
 * beyond any legitimate single pipeline step here while still bounding that blast radius.
 */
export const MAX_JOB_RUNTIME_CEILING_MS = 4 * 60 * 60_000;

/**
 * The runtime budget for one job: its own override, else its type's default, else the global fallback.
 *
 * Pure and total, so it can be unit-tested and so a bad value can never propagate. Both inputs are untrusted in
 * different ways — the row's came from Mongo and may predate a schema change or a careless caller, the type's from
 * whatever a definition declared — so anything that is not a positive finite number is DISCARDED rather than
 * coerced, and the next level down applies. Silently treating a bad value as `0` would make the job expire the
 * instant it starts, which reads in the logs as a mysteriously flapping job rather than as a bad config.
 *
 * Returns the resolved milliseconds and, when something was rejected or clamped, a `note` for the caller to log —
 * this function does not log itself, so it stays pure and callable per claim without generating noise.
 */
export function resolveJobRuntimeMs(
  rowValue: number | null | undefined,
  typeValue: number | undefined,
): { ms: number; note?: string } {
  const notes: string[] = [];

  // `null` is in the row's type on purpose: it is a Mongo document, and a field written as null by anything at all
  // arrives as null however the interface describes it. Treated as "unset" rather than as a rejected value, because
  // that is what it means.
  //
  // Returns the value if it can be used, else null — and records WHY it could not, so a rejected value is visible
  // in a log line rather than merely absent from the result.
  const usable = (value: number | null | undefined, label: string): number | null => {
    if (value === undefined || value === null) return null;
    if (!Number.isFinite(value) || value <= 0) {
      notes.push(`ignored-${label}:${String(value)}`);
      return null;
    }
    return value;
  };

  const requested = usable(rowValue, 'row') ?? usable(typeValue, 'type') ?? MAX_JOB_RUNTIME_MS;
  const ms = Math.min(requested, MAX_JOB_RUNTIME_CEILING_MS);
  if (requested > MAX_JOB_RUNTIME_CEILING_MS) {
    notes.push(`clamped:${Math.round(requested / 60_000)}min-to-${Math.round(ms / 60_000)}min`);
  }

  return notes.length ? { ms, note: notes.join(',') } : { ms };
}

/**
 * Extra time the sweeper waits beyond `MAX_JOB_RUNTIME_MS` before reclaiming a claim, and the reason the runtime
 * bound is NOT what protects against duplicate execution.
 *
 * A lease expiring is a database fact with no power over the worker: the row becomes reclaimable while instance A
 * carries on running. Relying on the timeout for correctness therefore meant relying on it being tuned above every
 * legitimately long job, and getting it wrong produced *silent concurrent duplicate execution* across instances.
 *
 * So the deadline is enforced where the work is instead. `execute` runs its own timer for `MAX_JOB_RUNTIME_MS` and
 * gives up on the handler itself — failing or requeuing the row under its `ClaimGuard` — and only then, this much
 * later, does the row become reclaimable to anyone else. The invariant that buys is worth stating plainly:
 *
 *   **The sweeper only ever cleans up after processes that are gone. It never takes work from a live one.**
 *
 * The margin needs to cover the gap between a local `setTimeout` firing and its terminal write landing — a network
 * round-trip, retries included. Two minutes is enormous for that and costs nothing, since it only delays recovery
 * from a crash that has already happened.
 *
 * What it does not survive is the process being frozen outright — SIGSTOP, a suspended VM, a pathological GC pause
 * longer than this margin — because then the local timer does not fire on time either. True immunity needs a
 * fencing token checked on every write, which is a great deal of machinery for a failure this deployment does not
 * have. Handlers are idempotent by contract, which is the backstop.
 */
export const LEASE_GRACE_MS = 2 * 60_000;

export const DEFAULT_MAX_ATTEMPTS = 3;

/** Backoff after a failed attempt, by attempt number (the last value repeats). */
export const RETRY_BACKOFF_MS = [30_000, 120_000, 600_000];

/**
 * How long a job waits before its condition is re-checked. ONE number, not a curve.
 *
 * A gate here is often a human: an exam parked on a teacher's confirmation waits days, and it stays `pending` the
 * whole time because readiness is derived rather than stored. Every expiry costs one exam read, one `ready()` call
 * and one `deferJob` write, so this sets what a backlog of parked exams costs: 1,500 of them is 1,500 reads and
 * writes every 30 seconds. That is affordable at this product's scale, and if it ever is not, raising this one
 * constant is the entire fix.
 *
 * There used to be a six-entry escalating table indexed by a step derived from `firstWaitAt` (2s → 15 min). It
 * bought a cheaper tail for parked exams, and it cost a table, a step constant, and a `waitAttempt()` function
 * computing which rung a job was on — three moving parts to tune a poll interval whose responsiveness is not
 * supposed to come from polling at all. `nudgeExamJobs` is the fast path — both confirmation mutations call it now,
 * so a teacher's sign-off releases work immediately — and 30 seconds is deliberately short enough that the system
 * stays usable even where someone forgets to.
 */
export const WAIT_BACKOFF_MS = 30_000;

export const SWEEP_INTERVAL_MS = 30_000;

/** Expired leases handled per sweep. */
export const EXPIRED_LEASE_BATCH = 50;

/** How long `done`/`cancelled` rows are kept. `failed` rows are never pruned — they are the record. */
export const PRUNE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/** Rows removed per prune pass. */
export const PRUNE_BATCH = 500;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// server/shutdown.ts — the stop budget
// ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * THREE TIMEOUTS IN A STRICT ORDER, and getting it wrong is silent. In one place for that reason:
 *
 *   SHUTDOWN_CLEANUP_MS + JOB_DRAIN_MS  <  SHUTDOWN_HARD_EXIT_MS  <  stop_grace_period  (deploy/*-compose.yml)
 *
 * Break the first inequality and the hard exit kills the process WHILE it is handing jobs back, leaving some
 * claimed rows unreclaimable for their full runtime budget. Break the second and Docker SIGKILLs mid-write, with
 * no log line at all. Both read as sensible in isolation, which is why the second is DERIVED from the first rather
 * than stated beside it — only the compose value is out of this file's reach.
 */

/**
 * How long claimed jobs get to finish on their own before they are handed back to the queue.
 *
 * Configurable mainly for DEVELOPMENT. `stop_grace_period` is a deadline, not a wait — the container stops the
 * moment the process exits — so an idle instance already stops in about a second. But a handler that ignores its
 * abort signal makes every `docker stop` cost this long, which is pure friction while iterating. `JOB_DRAIN_SEC=1`
 * removes it, and costs nothing in dev because a re-claimed job is just picked up again.
 */
export const JOB_DRAIN_MS = (positiveInt('JOB_DRAIN_SEC') ?? 15) * 1_000;

/**
 * How long the `cleanup` hooks get before the drain starts — closing the HTTP server, in practice.
 *
 * BOUNDED SEPARATELY, because it runs BEFORE the drain but under the same hard-exit deadline, so without a bound of
 * its own it silently eats the drain's budget. `server.close()` resolves only once every connection has ended, and a
 * keep-alive client that never sends another byte holds one open until its own timeout — so "closing the server" is
 * not reliably fast, and the job hand-back is the part that must not be skipped. Five seconds is generous for a
 * process whose listener has already been taken out of rotation by nginx (see dockerEntrypoint.sh).
 */
export const SHUTDOWN_CLEANUP_MS = 5_000;

/**
 * Past this, stop being graceful: log and exit non-zero rather than let the container be SIGKILLed.
 *
 * DERIVED FROM BOTH PHASES, so it cannot be configured into the wrong order. It was previously `JOB_DRAIN_MS + 10s`,
 * which was derived from the drain alone while the timer is armed before the cleanup hooks — so a slow HTTP close
 * came out of the drain's margin rather than out of its own. The remaining 10s covers what happens after the drain's
 * timeout: one guarded requeue per surviving job, bounded by concurrency and therefore by the heap.
 */
export const SHUTDOWN_HARD_EXIT_MS = SHUTDOWN_CLEANUP_MS + JOB_DRAIN_MS + 10_000;

/** Backoff lookup that clamps to the last entry instead of running off the end. */
export const backoffFor = (table: readonly number[], attempt: number): number =>
  table[Math.min(Math.max(attempt, 0), table.length - 1)] ?? table[table.length - 1] ?? 0;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// llm/llm-gate.ts — provider concurrency
// ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * How many instances of this process are expected to run against the same provider account.
 *
 * The gate is per-process and cannot be anything else — it counts promises in one Node heap. So the number
 * below is a FLEET budget divided by this, and an operator who scales to four replicas without saying so
 * quadruples what the provider actually sees. Defaults to 1, which is exactly the previous behaviour.
 */
const LLM_EXPECTED_REPLICAS = positiveInt('LLM_EXPECTED_REPLICAS') ?? 1;

/** The fleet-wide budget, before it is split across replicas. */
const LLM_FLEET_CAPACITY = positiveInt('LLM_MAX_CONCURRENT_CALLS') ?? 400;

/**
 * Model calls allowed in flight at once IN THIS PROCESS, across every lane. THE bound on provider concurrency
 * — job counts are not, because one job fans out over every task or submission it covers.
 */
export const LLM_CAPACITY = Math.max(1, Math.floor(LLM_FLEET_CAPACITY / LLM_EXPECTED_REPLICAS));

/**
 * Slots kept free for chat at all times: batch stops admitting while fewer than this many remain, so the next
 * teacher message finds room already waiting rather than queueing for it.
 *
 * CLAMPED TO `LLM_CAPACITY - 1`, because the reserve is subtracted from what batch may hold and a reserve equal
 * to the capacity leaves batch exactly zero slots — `active() + reserve < capacity` is then false at
 * `active() = 0` and **the batch lane never runs again**. At `LLM_CAPACITY = 1` the unclamped `ceil(1/4) = 1`
 * did precisely that, which is reachable by setting `LLM_MAX_CONCURRENT_CALLS=1` to debug, or by setting
 * `LLM_EXPECTED_REPLICAS` at or above the fleet budget. A reserve of 0 at capacity 1 is the honest answer:
 * one call at a time, chat and batch taking turns.
 */
export const LLM_INTERACTIVE_RESERVE = Math.min(LLM_CAPACITY - 1, Math.max(1, Math.ceil(LLM_CAPACITY / 4)));

/** A permit held longer than this is a leak, not a slow request. Logged. */
export const LLM_STUCK_PERMIT_MS = 10 * 60 * 1000;

/**
 * The outer deadline on a model call: past this the CALLER IS REJECTED and the permit reclaimed.
 *
 * A Node HTTP client can hang silently — a dropped connection with no keep-alive response leaves a promise that
 * never resolves AND never rejects. No `finally` runs, so the permit is held forever and, worse, whoever is
 * awaiting it waits forever too. Thirty held permits freeze the entire batch lane while chat carries on working,
 * and the awaiting job holds its lockKey for its whole runtime budget having done nothing at all.
 *
 * Set BELOW `MAX_JOB_RUNTIME_MS` deliberately. At twenty minutes the permit is reclaimed and the caller rejected, ten
 * minutes before the runner's own deadline would have abandoned the job — so the job fails with the real reason
 * attached and retries, instead of being killed by a timeout whose log line says only "too slow".
 *
 * GENEROUS BECAUSE THE ROUTER RETRIES UNDERNEATH, which is the part that is easy to miss. `@adornis/llm`'s
 * `LLMCompletions.create` runs 5 attempts with 2s/4s/6s/8s between them, and the OpenRouter handler builds its
 * `OpenAI` client without overriding that SDK's `maxRetries: 2` / `timeout: 600_000`. One `create()` can therefore
 * occupy a permit for 5 x 3 x 10 minutes of entirely legitimate retrying. So this is not a guard against an exotic
 * silent hang — it is the bound a merely slow-and-erroring provider reaches normally, and lowering it would start
 * cancelling calls that were going to succeed. Verified against @adornis/llm 30.0.31 and openai 6.22.0.
 *
 * Reclaiming the permit alone was not enough, and that was the earlier mistake. It fixed the gate's accounting
 * and left the real casualty — the stuck caller — exactly where it was. Rejecting the awaiting promise turns a
 * hang into an ordinary error: the call site's `finally` runs, the job fails, and the existing retry and backoff
 * machinery handles it like any other failure. Over-subscription for the moment a dead socket is still counted
 * is not a real cost, because the provider is not working on a dead socket.
 *
 * Generous on purpose. This is the boundary between "slow" and "never", not a per-request SLA; a call site that
 * wants a tighter bound should pass its own timeout.
 */
export const LLM_ABANDON_PERMIT_MS = 20 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * One line at boot with every number this file resolved, so a support question about "why is nothing running" can
 * be answered from a log rather than by reading three files.
 *
 * MUCH SHORTER THAN IT WAS, because most of what it reported no longer exists: heap budgets, per-job ceilings,
 * payload arithmetic, an RSS budget, CPU and memory-pressure thresholds. Concurrency is now one integer, which is
 * the point — see `JOB_CONCURRENCY` and the header of `load.ts`.
 */
export function logResolvedLimits(): void {
  logger.info(
    {
      admission: 'a job count, plus whatever back-pressure the model gate registers (see load.ts)',
      jobConcurrency: JOB_CONCURRENCY,
      tickIntervalMs: TICK_INTERVAL_MS,
      examScanLimit: EXAM_SCAN_LIMIT,
      pendingScanLimit: PENDING_SCAN_LIMIT,
      llmCapacityPerProcess: LLM_CAPACITY,
      llmInteractiveReserve: LLM_INTERACTIVE_RESERVE,
      maxJobRuntimeMinDefault: MAX_JOB_RUNTIME_MS / 60_000,
      maxJobRuntimeMinCeiling: MAX_JOB_RUNTIME_CEILING_MS / 60_000,
      leaseGraceMs: LEASE_GRACE_MS,
      defaultMaxAttempts: DEFAULT_MAX_ATTEMPTS,
      // Both halves of the stop budget, so the ordering against the compose `stop_grace_period` — the one value
      // this process cannot see — can be checked by reading a log line rather than by opening three files.
      shutdownCleanupMs: SHUTDOWN_CLEANUP_MS,
      jobDrainMs: JOB_DRAIN_MS,
      shutdownHardExitMs: SHUTDOWN_HARD_EXIT_MS,
      requiresComposeGraceAbove: `${Math.ceil(SHUTDOWN_HARD_EXIT_MS / 1000)}s`,
    },
    'Job system limits resolved',
  );
}
